import React, { useRef, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Paper, Divider, CircularProgress } from '@mui/material';
import MockTestCard from './MockTestCard';
import { MockTestSeries } from '../../models/MockTestSeries';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { batchCreateTestSessionData } from '../../store/slices/testSessionsSlice';
import { testSeriesConfig } from '../../data/seriesConfig';
import { loadSeriesWindows, getCachedSeriesWindows } from '../../data/seriesWindows';
import { selectIsMobileClassroom } from '../../store/selectors/studentSelectors';

interface MockTestSectionProps {
  mockTestSeries?: MockTestSeries[]; // Make optional for backward compatibility
}

// Session data is accessed from Redux state directly

const MockTestSection: React.FC<MockTestSectionProps> = ({ mockTestSeries }) => {
  const dispatch = useDispatch<AppDispatch>();
  const selectedStudent = useSelector((state: RootState) => state.students.selectedStudent);
  const allTestSessions = useSelector((state: RootState) => state.testSessions.sessionsBySeriesAndSubject);
  const isMobileClassroom = useSelector(selectIsMobileClassroom);

  // Add ref to track if API call is in progress
  const isCreatingSessionsRef = useRef(false);

  // Don't render Mock Test Section in mobile classroom mode
  if (isMobileClassroom) {
    return null;
  }
  
  useEffect(() => {
    // Validate that we have a proper student ID (not the default fallback values)
    const hasValidStudentId = selectedStudent?.id &&
                              selectedStudent.id !== 'N/A' &&
                              selectedStudent.id !== 'unknown-id' &&
                              !selectedStudent.id.startsWith('student-');

    if (hasValidStudentId && selectedStudent?.grade) {
      if (!isCreatingSessionsRef.current) {
        isCreatingSessionsRef.current = true;

        // Load the real schedule first, so sessions are labelled open or closed by
        // what the LMS says rather than by the dates bundled into the build.
        loadSeriesWindows(selectedStudent.grade).finally(() =>
        dispatch(batchCreateTestSessionData({
          studentId: selectedStudent.id,
          grade: selectedStudent.grade,
          studentName: selectedStudent.name,
          studentPhone: selectedStudent.phoneNumber,
          series: testSeriesConfig,
          autoAddQuestions: true
        })).finally(() => {
          isCreatingSessionsRef.current = false;
        }));
      }
    }
  }, [selectedStudent?.id, selectedStudent?.grade, dispatch, allTestSessions]);
  
  // Calculate days text for next test
  const calculateDaysText = (openingDate: string, closingDate: string) => {
    const currentDate = new Date();
    const openDate = new Date(openingDate);
    const closeDate = new Date(closingDate);
    
    let daysText = '';
    if (currentDate < openDate) {
      const daysUntilOpen = Math.ceil((openDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
      daysText = `${daysUntilOpen} days`;
    } else if (currentDate <= closeDate) {
      const daysUntilClose = Math.ceil((closeDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
      daysText = `${daysUntilClose} days`;
    }
    
    return daysText;
  };
  

  // Create series data structure from either config or mockTestSeries
  const seriesData = useMemo(() => {
    return testSeriesConfig || 
      (mockTestSeries && mockTestSeries.map(series => ({
        seriesName: series.seriesName,
        subjects: series.tests.map(test => ({ name: test.subject, color: test.color }))
      })));
  }, [mockTestSeries]);
    
  // If we have neither, show loading
  if (!seriesData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography 
        variant="h5" 
        sx={{ 
          mb: 3, 
          fontWeight: 600,
          color: '#1a1a1a'
        }}
      >
        Mock Test Series
      </Typography>
      
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid #e0e0e0',
          backgroundColor: '#fafafa'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {seriesData.map((series, seriesIndex) => {
            // For static data, use the series as is
            // For API data, check if we need to fetch
            const isLastSeries = seriesIndex === seriesData.length - 1;
            
            return (
              <Box key={seriesIndex} sx={{ mb: isLastSeries ? 0 : 4 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2, 
                    color: '#333',
                    fontSize: '1.1rem'
                  }}
                >
                  {series.seriesName}
                </Typography>
                <Grid container spacing={3}>
                  {series.subjects.map((subject, subjectIndex) => {
                    // Get test session data from the Redux store (accessed outside of render loop)
                    const testSession = allTestSessions[series.seriesName]?.[subject.name] || {
                      data: null,
                      status: 'idle',
                      error: null
                    };
                    
                    // Fallback to static data if available
                    const staticData = mockTestSeries && 
                      mockTestSeries[seriesIndex]?.tests?.find(
                        test => test.subject === subject.name
                      );
                      
                    // Determine data source and status
                    const isLoading = testSession.status === 'loading';
                    const hasError = testSession.status === 'failed';
                    const testData = testSession.data || staticData;
                    
                    // Create next test info
                    // Prefer the loaded schedule; series is matched by name, not by
                    // position, so a reordered config cannot pair a test with another
                    // series' dates.
                    const loadedWindow = selectedStudent?.grade
                      ? getCachedSeriesWindows(selectedStudent.grade)?.[series.seriesName]
                      : undefined;
                    const configWindow = testSeriesConfig.find(
                      (candidate) => candidate.seriesName === series.seriesName
                    );
                    const window = loadedWindow || configWindow;
                    const nextTestDays = calculateDaysText(
                      window?.openingDate || '',
                      window?.closingDate || ''
                    );
                    
                    // Create test object with next test info if we have data
                    const testWithInfo = testData && {
                      ...testData,
                      // Only use the calculated nextTestDays if Redux didn't provide a value
                      nextTestIn: testData.nextTestIn || nextTestDays,
                      seriesIndex,
                      testIndex: subjectIndex,
                    };

                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${series.seriesName}-${subject.name}`}>
                        {isLoading ? (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: 160,
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            bgcolor: '#ffffff'
                          }}>
                            <CircularProgress size={40} />
                          </Box>
                        ) : hasError ? (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: 160,
                            border: '1px solid #ffcccc',
                            borderRadius: 2,
                            bgcolor: '#fff8f8',
                            color: '#d32f2f',
                            p: 2,
                            textAlign: 'center'
                          }}>
                            Failed to load test data
                          </Box>
                        ) : testWithInfo ? (
                          <MockTestCard {...testWithInfo} />
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: 160,
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            bgcolor: '#ffffff',
                            color: '#757575',
                            p: 2,
                            textAlign: 'center'
                          }}>
                            No test data available
                          </Box>
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
                {!isLastSeries && (
                  <Divider 
                    sx={{ 
                      my: 2,
                      mx: -3,
                      height: '3px',
                      backgroundColor: '#000000',
                      borderRadius: 0
                    }} 
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default MockTestSection;